import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PublicReferralLink, fetchPublicReferralLinkRequest, recordReferralLinkVisitRequest } from '../api/referralLinksApi';

const ContactLandingPage: React.FC = () => {
    const { linkName } = useParams<{ linkName: string }>();
    const navigate = useNavigate();
    const [link, setLink] = useState<PublicReferralLink | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [codeCopied, setCodeCopied] = useState(false);

    useEffect(() => {
        if (linkName) {
            loadReferralLink(linkName);
        }
    }, [linkName]);

    const loadReferralLink = async (name: string) => {
        try {
            setLoading(true);
            setError(null);

            const linkData = await fetchPublicReferralLinkRequest(name);

            if (!linkData.isActive) {
                setError('This referral link is no longer active.');
                return;
            }

            setLink(linkData);

            // Fire-and-forget visit recording — we don't block the CTA on it.
            recordReferralLinkVisitRequest(name).catch(err =>
                console.error('Failed to record referral visit:', err),
            );
        } catch (err: any) {
            console.error('Failed to load referral link:', err);
            if (err.response?.status === 404) {
                setError('This referral link does not exist.');
            } else if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Unable to load this referral link. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCode = () => {
        if (!link?.code) return;
        navigator.clipboard.writeText(link.code);
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 1500);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
            >
                <CircularProgress size={60} sx={{ color: 'white' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: 2,
                }}
            >
                <Container maxWidth="sm">
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                        <Button variant="contained" onClick={() => navigate('/login')}>
                            Go to Login
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                py: 4,
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, sm: 5 },
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.98)',
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 2,
                            }}
                        >
                            {link?.title}
                        </Typography>

                        {link?.description && (
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                                {link.description}
                            </Typography>
                        )}

                        {link?.recruiterName && (
                            <Chip
                                icon={<PersonIcon />}
                                label={`Recruiter: ${link.recruiterName}`}
                                color="primary"
                                variant="outlined"
                                sx={{ mt: 1, mb: 3 }}
                            />
                        )}
                    </Box>

                    <Box sx={{ mt: 4 }}>
                        <Button
                            component="a"
                            href={link?.waMeUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="large"
                            fullWidth
                            startIcon={<WhatsAppIcon />}
                            disabled={!link?.waMeUrl}
                            sx={{
                                py: 1.75,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                backgroundColor: '#25D366',
                                '&:hover': { backgroundColor: '#1ebe5d' },
                            }}
                        >
                            Message us on WhatsApp
                        </Button>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                        >
                            Opens WhatsApp with a prefilled message — just press send.
                        </Typography>
                    </Box>

                    {link?.code && (
                        <Box
                            sx={{
                                mt: 4,
                                p: 3,
                                borderRadius: 2,
                                background: 'rgba(102, 126, 234, 0.08)',
                                border: '1px dashed rgba(102, 126, 234, 0.4)',
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Your reference code
                            </Typography>
                            <Tooltip title={codeCopied ? 'Copied!' : 'Tap to copy'} arrow>
                                <Box
                                    onClick={handleCopyCode}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        userSelect: 'all',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            letterSpacing: 4,
                                        }}
                                    >
                                        {link.code}
                                    </Typography>
                                    <ContentCopyIcon fontSize="small" color="action" />
                                </Box>
                            </Tooltip>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 1.5 }}
                            >
                                Keep this code in your message, or share it if someone asks how you found us.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            © 2025 Optima. All rights reserved.
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ContactLandingPage;
